import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-308d6828"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "live-record-page" }
const _hoisted_2 = { class: "live-record-page__consumer" }

import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Download } from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import { useRoute } from 'vue-router'
import { CrudSearch, CrudTable } from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import { useStore } from '@/store'
import type { CrudSelectOption } from '@/types/crud-config'
import type { CrudSort } from '@/types/crud'
import {
  createLiveRecordSearchItems,
  liveRecordTableColumns
} from './live-record.config'
import {
  exportLiveRecord,
  fetchLiveRecordFilterOptions,
  fetchLiveRecordPage
} from './live-record.service'
import type { LiveRecordFilters } from './live-record.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'Record',
  setup(__props) {

const route = useRoute()
const store = useStore()
const userInfo = store.state.login.userInfo
const isManager = String(userInfo.isManager) === '1'
const canList = usePermission('liveEventAccess', 'list')
const canDownload = usePermission('liveEventAccess', 'download')

function readScalarRouteQuery(name: 'channelId' | 'deptId' | 'empIds') {
  const value = route.query[name]
  if (value === undefined) return undefined
  if (typeof value !== 'string')
    throw new Error(`路由参数 ${name} 必须是字符串`)
  return value
}

function readRouteFilters(): Partial<LiveRecordFilters> {
  const accessTime = route.query.accessTime
  if (
    accessTime !== undefined &&
    (!Array.isArray(accessTime) ||
      accessTime.length !== 2 ||
      accessTime.some((value) => typeof value !== 'string'))
  ) {
    throw new Error('路由参数 accessTime 必须包含开始和结束时间')
  }

  const channelId = readScalarRouteQuery('channelId')
  const deptId = readScalarRouteQuery('deptId')
  const empIds = readScalarRouteQuery('empIds')
  return {
    ...(channelId !== undefined ? { channelId } : {}),
    ...(deptId !== undefined ? { deptId } : {}),
    ...(empIds !== undefined ? { empIds } : {}),
    ...(accessTime !== undefined ? { accessTime: accessTime as string[] } : {})
  }
}

let routeFilters: Partial<LiveRecordFilters> = {}
let routeError: Error | null = null
try {
  routeFilters = readRouteFilters()
} catch (cause) {
  routeError = cause instanceof Error ? cause : new Error('路由参数错误')
}

function createFilters(): LiveRecordFilters {
  return {
    channelId: '',
    consumerNickName: '',
    consumerWxUnionId: '',
    duration: '',
    accessTime: [
      dayjs().startOf('date').format('YYYY-MM-DD HH:mm:ss'),
      dayjs().endOf('date').format('YYYY-MM-DD HH:mm:ss')
    ],
    deptId: isManager ? userInfo.deptId : '',
    empIds: isManager ? '' : userInfo.id,
    ...routeFilters
  }
}

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  lastSuccessfulQuery,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  changeSort,
  refresh
} = usePagedCrud({
  createFilters,
  fetchPage: fetchLiveRecordPage
})

if (routeError) error.value = routeError

const optionsLoading = ref(false)
const exporting = ref(false)
const channelOptions = ref<CrudSelectOption[]>([])
const employeeOptions = ref<CrudSelectOption[]>([])
const departmentOptions: CrudSelectOption[] = store.state.login.depts.map(
  (item: { id: string | number; name: string }) => ({
    label: item.name,
    value: item.id
  })
)

const searchItems = computed(() =>
  createLiveRecordSearchItems({
    channelOptions: channelOptions.value,
    departmentOptions,
    employeeOptions: employeeOptions.value,
    isManager,
    optionsLoading: optionsLoading.value
  })
)
const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)
const canQuery = computed(() => canList && !routeError)

function runQuery(action: () => Promise<boolean>) {
  return canQuery.value ? action() : Promise.resolve(false)
}

function handleSortChange(value: {
  prop?: string
  order?: 'ascending' | 'descending' | null
}) {
  const sort: CrudSort | undefined =
    value.prop && value.order
      ? { prop: value.prop, order: value.order }
      : undefined
  return runQuery(() => changeSort(sort))
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

async function download() {
  if (!lastSuccessfulQuery.value) return

  exporting.value = true
  try {
    saveBlob(
      await exportLiveRecord(lastSuccessfulQuery.value),
      '直播访问记录.xlsx'
    )
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '导出失败')
  } finally {
    exporting.value = false
  }
}

async function loadFilterOptions() {
  optionsLoading.value = true
  try {
    const options = await fetchLiveRecordFilterOptions()
    channelOptions.value = options.channels.map((item) => ({
      label: item.eventName,
      value: item.rowId
    }))
    employeeOptions.value = options.employees.map((item) => ({
      label: item.realName,
      value: item.id
    }))
  } catch (cause) {
    ElMessage.error(
      cause instanceof Error ? cause.message : '筛选项加载失败，请稍后重试'
    )
  } finally {
    optionsLoading.value = false
  }
}

onMounted(() => {
  loadFilterOptions()
  runQuery(load)
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_avatar = _resolveComponent("el-avatar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: searchItems.value,
      loading: _unref(loading),
      onSearch: _cache[0] || (_cache[0] = ($event: any) => (runQuery(_unref(search)))),
      onReset: _cache[1] || (_cache[1] = ($event: any) => (runQuery(_unref(reset))))
    }, null, 8, ["model", "items", "loading"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(liveRecordTableColumns),
      onPageChange: _cache[2] || (_cache[2] = ($event: any) => (runQuery(() => _unref(changePage)($event)))),
      onPageSizeChange: _cache[3] || (_cache[3] = ($event: any) => (runQuery(() => _unref(changePageSize)($event)))),
      onSortChange: handleSortChange,
      onRetry: _cache[4] || (_cache[4] = ($event: any) => (runQuery(_unref(refresh))))
    }, {
      title: _withCtx(() => [
        _createTextVNode("访问记录")
      ]),
      actions: _withCtx(() => [
        (_unref(canDownload))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              loading: exporting.value,
              disabled: !_unref(lastSuccessfulQuery),
              onClick: download
            }, {
              default: _withCtx(() => [
                (!exporting.value)
                  ? (_openBlock(), _createBlock(_unref(Download), {
                      key: 0,
                      class: "live-record-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 导出 ")
              ]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true)
      ]),
      consumer: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_avatar, {
            shape: "square",
            size: 60,
            src: row.consumerHeadImgUrl
          }, null, 8, ["src"]),
          _createElementVNode("span", null, _toDisplayString(row.consumerNickName), 1)
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns"])
  ]))
}
}

})